import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FeedItem } from "../model/feedReducer";
import { renderAssistantMarkdown } from "../model/renderAssistantMarkdown";
import { isHttpUrl } from "../model/safeUrl";
import type { DeepLinkCard, EntityCard } from "../model/types";
import { DeepLinkButtons } from "./DeepLinkButtons";
import { EntityCardView } from "./EntityCardView";
import { OptionButtons } from "./OptionButtons";
import { PlanCard } from "./PlanCard";

export interface AssistantMessageItemProps {
  item: FeedItem;
  isLast: boolean;
  busy: boolean;
  onOption: (option: string) => void;
  onExecute: (planId: string, confirmedSteps: number[]) => void;
  onCancel: (planId: string) => Promise<unknown> | undefined;
}

/** Одно сообщение ленты: реплика сотрудника или ответ помощника со всеми вложениями. */
export const AssistantMessageItem = memo(function AssistantMessageItem({
  item,
  isLast,
  busy,
  onOption,
  onExecute,
  onCancel,
}: AssistantMessageItemProps) {
  const { t } = useTranslation();
  // Разбор markdown только при смене текста: поток меняет лишь последний
  // элемент ленты, остальные сообщения не перерисовываются (memo).
  const assistantText = item.kind === "assistant" ? item.text : "";
  const markdown = useMemo(
    () => (assistantText ? renderAssistantMarkdown(assistantText) : null),
    [assistantText]
  );

  if (item.kind === "user") {
    const photos = item.attachments.filter((a) => isHttpUrl(a.url));
    return (
      <Box
        sx={{
          alignSelf: "flex-end",
          maxWidth: "85%",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          borderBottomRightRadius: 4,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        {photos.length > 0 && (
          <Stack
            direction="row"
            gap={0.5}
            flexWrap="wrap"
            sx={{ mb: item.text ? 0.75 : 0 }}
          >
            {photos.map((a) => (
              <Box
                key={a.id}
                component="img"
                src={a.url}
                alt=""
                loading="lazy"
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: "cover",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              />
            ))}
          </Stack>
        )}
        {item.text && (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {item.text}
          </Typography>
        )}
      </Box>
    );
  }

  const entities = item.cards.filter(
    (c): c is EntityCard => c.type === "entity"
  );
  const links = item.cards.filter(
    (c): c is DeepLinkCard => c.type === "deep_link"
  );
  const thinking = item.pending && !item.text && !item.plan && !item.ask;

  return (
    <Box
      sx={{
        maxWidth: "100%",
        minWidth: 0,
        wordBreak: "break-word",
        "& > :first-child": { mt: 0 },
      }}
    >
      {thinking && (
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={14} thickness={5} />
          <Typography variant="body2" color="text.secondary">
            {t("partnerAssistant.thinking")}
          </Typography>
        </Stack>
      )}
      {item.offTopic && (
        <Typography variant="body2" color="text.secondary">
          {t("partnerAssistant.offTopic")}
        </Typography>
      )}
      {markdown}
      {item.ask && (
        <OptionButtons
          options={item.ask.options}
          disabled={!isLast || busy}
          onSelect={onOption}
        />
      )}
      {item.plan && (
        <PlanCard
          key={item.plan.id}
          plan={item.plan}
          busy={busy}
          onExecute={onExecute}
          onCancel={onCancel}
        />
      )}
      {entities.length > 0 && (
        <Stack gap={1} sx={{ mt: 1 }}>
          {entities.map((c, i) => (
            <EntityCardView key={`${c.entityType}-${c.id ?? i}`} card={c} />
          ))}
        </Stack>
      )}
      <DeepLinkButtons links={links} />
      {item.error && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ mt: 0.75, color: "error.main" }}
        >
          <ErrorOutlineRounded sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            {t("partnerAssistant.errors.generic")}
          </Typography>
        </Stack>
      )}
    </Box>
  );
});
